import { useCallback, useEffect, useState } from 'react';

import type { CommunicationBanner as CommunicationBannerData } from '@/types/models/CommunicationBanner';

type UseCommunicationBannerParams = {
  banner: CommunicationBannerData;
};

type UseCommunicationBannerReturn = {
  hasCta: boolean;
  isExternalCta: boolean;
  isVisible: boolean;
  handleDismiss: () => void;
};

const isExternalUrl = (url: string) => {
  if (typeof window === 'undefined') return false;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const getDismissedFromStorage = (storageKey: string) => {
  try {
    return sessionStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const setDismissedInStorage = (storageKey: string) => {
  try {
    sessionStorage.setItem(storageKey, 'true');
    return true;
  } catch {
    return false;
  }
};

export const useCommunicationBanner = ({
  banner,
}: UseCommunicationBannerParams): UseCommunicationBannerReturn => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasCheckedDismissal, setHasCheckedDismissal] = useState(!banner.dismissable);
  const [isExternalCta, setIsExternalCta] = useState(false);

  const dismissStorageKey = `communication-banner:dismissed:${banner.moment}:${banner.id}`;
  const hasCta = !!banner.ctaLabel && !!banner.ctaUrl;

  useEffect(() => {
    if (!banner.dismissable) {
      setIsDismissed(false);
      setHasCheckedDismissal(true);
      return;
    }

    setIsDismissed(getDismissedFromStorage(dismissStorageKey));
    setHasCheckedDismissal(true);
  }, [banner.dismissable, dismissStorageKey]);

  useEffect(() => {
    if (!banner.ctaUrl) {
      setIsExternalCta(false);
      return;
    }

    setIsExternalCta(isExternalUrl(banner.ctaUrl));
  }, [banner.ctaUrl]);

  const handleDismiss = useCallback(() => {
    setDismissedInStorage(dismissStorageKey);
    setIsDismissed(true);
  }, [dismissStorageKey]);

  return {
    hasCta,
    isExternalCta,
    isVisible: hasCheckedDismissal && !isDismissed,
    handleDismiss,
  };
};
